import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import dataService from '../services/dataService';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS_URL, getImageUrl } from '../config/apiConfig';
import { toast } from 'react-toastify';
import {
    CheckCircleIcon,
    XCircleIcon,
    UserIcon,
    MapPinIcon,
    EllipsisVerticalIcon,
    EyeIcon,
    PencilSquareIcon,
    NoSymbolIcon,
    TrashIcon,
    PhoneIcon,
    EnvelopeIcon,
    BriefcaseIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import MemberDetailsModal from '../components/MemberDetailsModal';
import MemberEditForm from '../components/MemberEditForm';
import { twMerge } from 'tailwind-merge';
import { createPortal } from 'react-dom';
import { formatCategoriesForDropdown } from '../utils/categoryHelper';

const ManageUsers = () => {
    const navigate = useNavigate();
    const { orgCode } = useParams();
    const { user: currentUser } = useAuth();
    const location = useLocation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    // Check if there's a defaultTab in navigation state, otherwise default to 'approved'
    const [activeTab, setActiveTab] = useState(location.state?.defaultTab || 'approved'); // approved, pending, inactive, deleted, deactivation-pending
    const [deactivationRequests, setDeactivationRequests] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterSelectedChapter, setFilterSelectedChapter] = useState('');
    const [filterSelectedCategory, setFilterSelectedCategory] = useState('');
    const [filterChapters, setFilterChapters] = useState([]);
    const [filterCategories, setFilterCategories] = useState([]);
    const itemsPerPage = 12;
    const isSuperAdmin = currentUser?.chapter === null;
    const chapterLabel = currentUser?.namingConvention?.chapterLabel || 'Chapter';
    const categoryLabel = currentUser?.namingConvention?.categoryLabel || 'Category';

    const handleActivate = async (id) => {
        try {
            await dataService.activateUser(id);
            setUsers(users.filter(u => u.id !== id));
            toast.success('User activated');
        } catch (error) {
            if (error.response && error.response.status === 409 && error.response.data?.code === 'CATEGORY_TAKEN') {
                setActivationUserId(id);
                try {
                    const [catRes, approvedRes] = await Promise.all([
                        dataService.getMasterCategories(),
                        dataService.getApprovedUsers()
                    ]);
                    
                    const userToActivate = users.find(u => u.id === id);
                    const userChapter = userToActivate?.chapter;
                    const approvedUsers = approvedRes.data?.data || [];
                    
                    // Filter out category IDs already taken by approved/active members in the same chapter
                    const takenCategoryIds = approvedUsers
                        .filter(u => u.chapter === userChapter && u.id !== id)
                        .map(u => u.category_id)
                        .filter(Boolean);
                    
                    const allCategories = catRes.data?.data || [];
                    const availableCategories = allCategories.filter(c => !takenCategoryIds.includes(c.id));
                    
                    setCategories(formatCategoriesForDropdown(availableCategories));
                } catch (catErr) {
                    console.error("Failed to load categories", catErr);
                }
                setIsActivationCategoryModalOpen(true);
                toast.warning('Member category is already taken. Please choose a different category to activate.');
            } else {
                toast.error(error.response?.data?.message || 'Activation failed');
            }
        }
    };

    const handleActivateWithCategory = async (e) => {
        e.preventDefault();
        if (!selectedCategoryId) {
            toast.warning('Please select a category');
            return;
        }
        try {
            await dataService.activateUser(activationUserId, { categoryId: selectedCategoryId });
            setUsers(users.filter(u => u.id !== activationUserId));
            toast.success('User activated and category updated');
            setIsActivationCategoryModalOpen(false);
            setActivationUserId(null);
            setSelectedCategoryId('');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Activation failed');
        }
    };

    // Modal State
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [modalMode, setModalMode] = useState('view'); // view, edit

    // Deactivation Modal State
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
    const [deactivateUserId, setDeactivateUserId] = useState(null);
    const [deactivateRequestId, setDeactivateRequestId] = useState(null);
    const [deactivateReason, setDeactivateReason] = useState('');
    const [deactivateType, setDeactivateType] = useState('immediate'); // immediate, scheduled
    const [deactivateDate, setDeactivateDate] = useState('');
    const [deactivateModalMode, setDeactivateModalMode] = useState('direct'); // direct, approve

    // Category Taken / Activation Modal State
    const [isActivationCategoryModalOpen, setIsActivationCategoryModalOpen] = useState(false);
    const [activationUserId, setActivationUserId] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    useEffect(() => {
        const uniqueChapters = [...new Set(users.map(u => u.chapter).filter(Boolean))].sort();
        const uniqueCategories = [...new Set(users.map(u => u.category_name || u.category_title).filter(Boolean))].sort();
        setFilterChapters(uniqueChapters);
        setFilterCategories(uniqueCategories);
    }, [users]);

    // Reset to page 1 when search, filters, or tab changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterSelectedChapter, filterSelectedCategory, activeTab]);

    const isExpired = (user) => {
        if (!user || user.status !== 'approved') return false;
        if (!user.membership_renewal_date) return false;
        const renewalDate = new Date(user.membership_renewal_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return renewalDate < today;
    };

    const fetchData = async () => {
        setLoading(true);
        console.log('Fetching data for tab:', activeTab);
        try {
            let res;
            if (activeTab === 'approved') {
                res = await dataService.getApprovedUsers();
                let allApproved = res.data.data || [];
                // Chapter Admin -> only own chapter members (Backend already handles this, but keeping for safety)
                if (currentUser?.role === 'chapter_admin') {
                    allApproved = allApproved.filter(
                        u => u.chapter === currentUser.chapter
                    );
                }
                // Filter out expired members (User request: "don't want that expired members in the list")
                setUsers(allApproved.filter(u => !isExpired(u)));
            } else if (activeTab === 'inactive') {
                // Fetch finalized deactivations
                res = await dataService.getDeactivatedUsers();
                let inactiveUsers = res.data.data || [];

                if (currentUser?.role === 'chapter_admin') {
                    inactiveUsers = inactiveUsers.filter(
                        u => u.chapter === currentUser.chapter
                    );
                }

                setUsers(inactiveUsers);
            } else if (activeTab === 'deactivation-pending') {
                // Fetch members with deactivation pending status
                res = await dataService.getDeactivatedPendingUsers();
                let pendingUsers = res.data.data || [];

                if (currentUser?.role === 'chapter_admin') {
                    pendingUsers = pendingUsers.filter(
                        u => u.chapter === currentUser.chapter
                    );
                }

                setUsers(pendingUsers);
            } else if (activeTab === 'deleted') {
                res = await dataService.getDeletedUsers();
                let deletedUsers = res.data.data || [];
                
                if (currentUser?.role === 'chapter_admin') {
                    deletedUsers = deletedUsers.filter(
                        u => u.chapter === currentUser.chapter
                    );
                }
                
                setUsers(deletedUsers);
            }

        } catch (error) {
            console.error('Fetch error:', error);
            let errorMsg = 'Failed to fetch users';

            if (error.response) {
                // Server responded with a status code
                errorMsg = error.response.data?.message || `Server Error(${error.response.status})`;
            } else if (error.request) {
                // Request made but no response
                errorMsg = 'Network Error: No response from server';
            } else {
                // Something else happened
                errorMsg = error.message;
            }

            toast.error(errorMsg, { autoClose: 5000 });
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        try {
            await dataService.approveUser(id);
            setUsers(users.filter(u => u.id !== id));
            toast.success('User approved');
        } catch (error) {
            toast.error('Approval failed');
        }
    };

    const handleReject = async (id) => {
        try {
            await dataService.rejectUser(id);
            setUsers(users.filter(u => u.id !== id));
            toast.success('User rejected');
        } catch (error) {
            toast.error('Rejection failed');
        }
    };

    const handleDeactivate = (id) => {
        setDeactivateUserId(id);
        setDeactivateRequestId(null);
        setDeactivateReason('');
        setDeactivateType('immediate');
        setDeactivateDate('');
        setDeactivateModalMode('direct');
        setIsDeactivateModalOpen(true);
    };

    const submitDeactivation = async () => {
        if (deactivateModalMode === 'direct') {
            if (!deactivateReason.trim()) {
                toast.error('Reason for deactivation is required');
                return;
            }
            if (deactivateType === 'scheduled' && !deactivateDate) {
                toast.error('Please select a deactivation date');
                return;
            }

            try {
                const res = await dataService.deactivateUser(deactivateUserId, {
                    reason: deactivateReason,
                    deactivationDate: deactivateType === 'scheduled' ? deactivateDate : null
                });

                if (res.data.isPending) {
                    toast.info(res.data.message);
                } else {
                    toast.success(res.data.message);
                }
                setIsDeactivateModalOpen(false);
                fetchData();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Deactivation failed');
            }
        } else {
            if (deactivateType === 'scheduled' && !deactivateDate) {
                toast.error('Please select a deactivation date');
                return;
            }

            try {
                await dataService.approveDeactivationRequest(deactivateRequestId, {
                    deactivationDate: deactivateType === 'scheduled' ? deactivateDate : null
                });
                toast.success('Deactivation request approved');
                setIsDeactivateModalOpen(false);
                fetchData();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to approve request');
            }
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) return;
        try {
            await dataService.deleteUser(id);
            setUsers(users.filter(u => u.id !== id));
            toast.success('User deleted');
        } catch (error) {
            toast.error('Deletion failed');
        }
    };

    const handleUpdateMember = async (id, data) => {
        try {
            await dataService.adminUpdateUser(id, data);
            toast.success('Member details updated');
            setIsDetailsModalOpen(false);
            fetchData(); // Refresh list
        } catch (error) {
            toast.error('Update failed');
        }
    };

    const handleApproveDeactivation = (requestId) => {
        setDeactivateUserId(null);
        setDeactivateRequestId(requestId);
        setDeactivateReason('');
        setDeactivateType('immediate');
        setDeactivateDate('');
        setDeactivateModalMode('approve');
        setIsDeactivateModalOpen(true);
    };

    const handleRejectDeactivation = async (requestId) => {
        if (!window.confirm('Reject this deactivation request?')) return;
        try {
            await dataService.rejectDeactivationRequest(requestId);
            toast.success('Deactivation request rejected');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reject request');
        }
    };

    const openModal = (user, mode) => {
        setSelectedUser(user);
        setModalMode(mode);
        setIsDetailsModalOpen(true);
    };

    const tabs = [
        { id: 'approved', label: 'All Members' },
        { id: 'inactive', label: 'Deactivated Members' },
        { id: 'deactivation-pending', label: 'Deactivation Requests' },
        { id: 'deleted', label: 'Deleted Members', roles: ['admin', 'super_admin'] },
    ].filter(tab => !tab.roles || tab.roles.includes(currentUser?.role));

    // Filter users based on search query and dropdowns
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (user.chapter && user.chapter.toLowerCase().includes(searchQuery.toLowerCase()));
            
        const matchesChapter = filterSelectedChapter === '' || user.chapter === filterSelectedChapter;
        const userCategory = user.category_name || user.category_title;
        const matchesCategory = filterSelectedCategory === '' || userCategory === filterSelectedCategory;

        return matchesSearch && matchesChapter && matchesCategory;
    });

    // Pagination logic for approved tab
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = activeTab === 'approved' ? filteredUsers.slice(startIndex, endIndex) : filteredUsers;
    const displayUsers = activeTab === 'approved' ? paginatedUsers : filteredUsers;

    return (
        <div className="space-y-8">
            <div className="premium-card p-8 bg-white">
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
                    <div className="animate-fade-in">
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Member Directory</h1>
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" />
                            <p className="text-[13px] font-semibold text-slate-500 uppercase tracking-widest">
                                {filteredUsers.length} {activeTab === 'approved' ? 'Active' : activeTab} members
                            </p>
                        </div>
                    </div>

                    {/* Search Bar and Filters */}
                    <div className="w-full xl:w-auto flex flex-col md:flex-row items-center gap-4 lg:gap-6">
                        
                        {/* Filters Group */}
                        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto p-1.5 bg-slate-100/50 rounded-2xl border border-slate-200/60">
                                <select
                                    value={filterSelectedChapter}
                                    onChange={(e) => setFilterSelectedChapter(e.target.value)}
                                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all shadow-sm min-w-[140px]"
                                >
                                    <option value="">All {chapterLabel}s</option>
                                    {filterChapters.map((chapter, index) => (
                                        <option key={index} value={chapter}>{chapter}</option>
                                    ))}
                                </select>

                                <select
                                    value={filterSelectedCategory}
                                    onChange={(e) => setFilterSelectedCategory(e.target.value)}
                                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all shadow-sm min-w-[140px]"
                                >
                                    <option value="">All {categoryLabel}s</option>
                                    {filterCategories.map((category, index) => (
                                        <option key={index} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Search Input */}
                            <div className="relative w-full md:w-64 lg:w-80">
                                <input
                                    type="text"
                                    placeholder="Search members..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full px-4 py-3 pl-11 pr-10 bg-slate-50 border-2 border-slate-200 rounded-xl text-sm font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:border-primary-300 focus:bg-white transition-all"
                                />
                                <svg
                                    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                </div>

                <div className="flex bg-slate-100/80 p-1.5 rounded-2xl border border-slate-200/50 shadow-inner backdrop-blur-sm self-stretch xl:self-auto overflow-x-auto whitespace-nowrap scrollbar-hide">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={twMerge(
                                "px-6 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-200 uppercase tracking-widest",
                                activeTab === tab.id
                                    ? "bg-white shadow-md text-primary-600 ring-1 ring-slate-100"
                                    : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-32 premium-card">
                    <div className="relative">
                        <div className="w-12 h-12 rounded-full border-4 border-slate-100 border-t-primary-600 animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-primary-600 animate-ping" />
                        </div>
                    </div>
                    <p className="mt-4 text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">Synchronizing</p>
                </div>
            ) : (
                <div className="premium-card transition-all duration-500">
                    {displayUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 text-slate-300">
                            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 mb-4">
                                <UserIcon className="w-12 h-12 opacity-20" />
                            </div>
                            <p className="text-sm font-bold uppercase tracking-widest opacity-40">
                                {searchQuery || filterSelectedChapter || filterSelectedCategory ? 'No members found matching your filters' : 'No records found'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6 bg-white">
                                {displayUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        className="group relative bg-white border-2 border-slate-100 rounded-[2rem] p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                                    >
                                        <div>
                                            {/* Profile Header */}
                                            <div className="flex items-start gap-4 mb-6">
                                                <div className="relative flex-shrink-0">
                                                    <div className="w-16 h-16 rounded-2xl bg-white overflow-hidden border-2 border-white shadow-sm ring-2 ring-slate-100 group-hover:ring-primary-200 transition-all">
                                                        {user.profile_image ? (
                                                            <img
                                                                src={getImageUrl(user.profile_image)}
                                                                alt={user.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-slate-300 bg-slate-50">
                                                                <UserIcon className="w-8 h-8" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={twMerge(
                                                        "absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white shadow-sm",
                                                        isExpired(user) ? 'bg-amber-500' :
                                                            (user.status === 'approved' || user.status === 'active_pending') ? (user.scheduled_deactivation_date || user.status === 'active_pending' ? 'bg-amber-500' : 'bg-emerald-500') :
                                                                user.status === 'inactive' ? 'bg-red-500' :
                                                                    user.status === 'deleted' ? 'bg-gray-500' : 'bg-amber-400'
                                                    )} />
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-base font-black text-slate-900 group-hover:text-primary-600 transition-colors tracking-tight truncate">
                                                        {user.name}
                                                    </h3>
                                                    
                                                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                                        <span className={twMerge(
                                                            "inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider border shadow-sm",
                                                            isExpired(user)
                                                                ? 'bg-amber-50 text-amber-700 border-amber-100'
                                                                : activeTab === 'deactivation-pending'
                                                                    ? 'bg-orange-50 text-orange-700 border-orange-100'
                                                                    : (user.status === 'approved' || user.status === 'active_pending')
                                                                        ? (user.scheduled_deactivation_date || user.status === 'active_pending' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')
                                                                        : user.status === 'inactive'
                                                                            ? 'bg-red-50 text-red-700 border-red-100'
                                                                            : user.status === 'deleted'
                                                                                ? 'bg-gray-50 text-gray-700 border-gray-100'
                                                                                : 'bg-amber-50 text-amber-700 border-amber-100'
                                                        )}>
                                                            {isExpired(user)
                                                                ? 'Expired'
                                                                : activeTab === 'deactivation-pending'
                                                                    ? 'Pending Deact.'
                                                                    : ((user.status === 'approved' || user.status === 'active_pending') && (user.scheduled_deactivation_date || user.status === 'active_pending'))
                                                                        ? 'Deact. Scheduled'
                                                                        : (user.status === 'approved' || user.status === 'active_pending')
                                                                            ? 'Active'
                                                                            : user.status}
                                                        </span>

                                                        {(user.status === 'approved' || user.status === 'active_pending') && !user.scheduled_deactivation_date && user.created_at && (new Date() - new Date(user.created_at)) / (1000 * 60 * 60) <= 48 && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider bg-primary-50 text-primary-600 border border-primary-100 animate-pulse">
                                                                New
                                                             </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-1 gap-3 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 mb-6">
                                                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 min-w-0">
                                                    <EnvelopeIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                                    <span className="truncate">{user.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                                                    <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                                    <span>{user.chapter || 'Global'}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <BriefcaseIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                                    <span className="font-black text-slate-500 uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded-md text-[10px] truncate max-w-full">
                                                        {currentUser?.role === 'chapter_admin' && user.chapter === currentUser.chapter
                                                            ? 'Member'
                                                            : (user.category_name || user.category_title || 'Member')}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Scheduling Information Block */}
                                            {activeTab === 'deactivation-pending' && (
                                                <div className="mb-6 p-3 bg-orange-50 rounded-xl border border-orange-100">
                                                    <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-wider text-orange-600">
                                                        <span>⏳ Pending Deactivation</span>
                                                        <span>Since: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {(user.status === 'approved' || user.status === 'active_pending') && user.scheduled_deactivation_date && (
                                                <div className="mb-6 p-3 bg-amber-50 rounded-xl border border-amber-100">
                                                    <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-wider text-amber-600">
                                                        <span>⏳ Deactivation Scheduled</span>
                                                        <span className="bg-amber-100 px-1.5 py-0.5 rounded-md text-[8px]">
                                                            On: {new Date(user.scheduled_deactivation_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions Footer */}
                                        <div className="flex items-center justify-end pt-4 border-t border-slate-50">
                                            {activeTab === 'deactivation-pending' ? (
                                                <Menu as="div" className="relative w-full inline-block text-left">
                                                    <Menu.Button className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl font-bold text-xs transition-all border border-slate-100 shadow-sm">
                                                        <span>Manage Actions</span>
                                                        <EllipsisVerticalIcon className="w-4 h-4 shrink-0" />
                                                    </Menu.Button>

                                                    <Transition
                                                        as={Fragment}
                                                        enter="transition ease-out duration-200"
                                                        enterFrom="transform opacity-0 scale-95 translate-y-2"
                                                        enterTo="transform opacity-100 scale-100 translate-y-0"
                                                        leave="transition ease-in duration-150"
                                                        leaveFrom="transform opacity-100 scale-100 translate-y-0"
                                                        leaveTo="transform opacity-0 scale-95 translate-y-2"
                                                    >
                                                        <Menu.Items className="absolute right-0 bottom-full mb-2 w-56 origin-bottom-right bg-white rounded-2xl shadow-2xl border border-slate-100 focus:outline-none z-50 overflow-hidden py-1.5">
                                                            <div className="px-1.5 space-y-0.5">
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={() => navigate(`/${orgCode}/admin/members/${user.id}`)}
                                                                            className={twMerge(
                                                                                "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                active ? "bg-primary-50 text-primary-600" : "text-slate-700"
                                                                            )}
                                                                        >
                                                                            <EyeIcon className="h-4 w-4 text-slate-400" />
                                                                            View Profile
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>

                                                                {currentUser?.role === 'admin' && (
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => openModal(user, 'edit')}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-primary-50 text-primary-600" : "text-slate-700"
                                                                                )}
                                                                            >
                                                                                <PencilSquareIcon className="h-4 w-4 text-slate-400" />
                                                                                Modify Details
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                )}
                                                            </div>

                                                            {currentUser?.role === 'admin' && (
                                                                <div className="px-1.5 pt-1.5 mt-1.5 border-t border-slate-50">
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => handleActivate(user.id)}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-emerald-50 text-emerald-600" : "text-slate-700"
                                                                                )}
                                                                            >
                                                                                <ArrowPathIcon className="h-4 w-4 text-slate-400" />
                                                                                Activate Member
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>

                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => handleDeactivate(user.id)}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-red-50 text-red-600" : "text-slate-700"
                                                                                )}
                                                                            >
                                                                                <NoSymbolIcon className="h-4 w-4 text-slate-400" />
                                                                                Deactivate Member
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                </div>
                                                            )}
                                                        </Menu.Items>
                                                    </Transition>
                                                </Menu>
                                            ) : (
                                                <Menu as="div" className="relative w-full inline-block text-left">
                                                    <Menu.Button className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl font-bold text-xs transition-all border border-slate-100 shadow-sm">
                                                        <span>Manage Actions</span>
                                                        <EllipsisVerticalIcon className="w-4 h-4 shrink-0" />
                                                    </Menu.Button>
                                                    <Transition
                                                        as={Fragment}
                                                        enter="transition ease-out duration-200"
                                                        enterFrom="transform opacity-0 scale-95 translate-y-2"
                                                        enterTo="transform opacity-100 scale-100 translate-y-0"
                                                        leave="transition ease-in duration-150"
                                                        leaveFrom="transform opacity-100 scale-100 translate-y-0"
                                                        leaveTo="transform opacity-0 scale-95 translate-y-2"
                                                    >
                                                        <Menu.Items className="absolute right-0 bottom-full mb-2 w-56 origin-bottom-right bg-white rounded-2xl shadow-2xl border border-slate-100 focus:outline-none z-50 overflow-hidden py-1.5">
                                                            <div className="px-1.5 space-y-0.5">
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={() => navigate(`/${orgCode}/admin/members/${user.id}`)}
                                                                            className={twMerge(
                                                                                "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                active ? "bg-primary-50 text-primary-600" : "text-slate-700"
                                                                            )}
                                                                        >
                                                                            <EyeIcon className="h-4 w-4 text-slate-400" />
                                                                            View Profile
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>
                                                                {activeTab !== 'deleted' && (!['inactive', 'deactivation-pending'].includes(activeTab) || currentUser?.role === 'admin') && (
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => openModal(user, 'edit')}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-primary-50 text-primary-600" : "text-slate-700"
                                                                                )}
                                                                            >
                                                                                <PencilSquareIcon className="h-4 w-4 text-slate-400" />
                                                                                {activeTab === 'pending' ? 'Review & Approve' : 'Modify Details'}
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                )}
                                                            </div>

                                                            {activeTab === 'pending' && ['admin', 'chapter_admin'].includes(currentUser?.role) && (
                                                                 <div className="px-1.5 pt-1.5 mt-1.5 border-t border-slate-50">
                                                                     <Menu.Item>
                                                                         {({ active }) => (
                                                                             <button
                                                                                 onClick={() => handleReject(user.id)}
                                                                                 className={twMerge(
                                                                                     "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                     active ? "bg-red-50 text-red-600" : "text-slate-700"
                                                                                 )}
                                                                             >
                                                                                 <XCircleIcon className="h-4 w-4 text-slate-400" />
                                                                                 Reject Registration
                                                                             </button>
                                                                         )}
                                                                     </Menu.Item>
                                                                 </div>
                                                             )}

                                                            {(activeTab === 'approved' || activeTab === 'pending') && ['admin', 'chapter_admin'].includes(currentUser?.role) && (
                                                                <div className="px-1.5 pt-1.5 mt-1.5 border-t border-slate-50">
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => handleDeactivate(user.id)}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-red-50 text-red-600" : "text-slate-700"
                                                                                )}
                                                                            >
                                                                                <NoSymbolIcon className="h-4 w-4 text-slate-400" />
                                                                                Deactivate
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                </div>
                                                            )}

                                                            {(activeTab === 'inactive' || activeTab === 'deactivation-pending') && currentUser?.role === 'admin' && (
                                                                <div className="px-1.5 pt-1.5 mt-1.5 border-t border-slate-50">
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => handleActivate(user.id)}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-emerald-50 text-emerald-600" : "text-slate-700"
                                                                                )}
                                                                            >
                                                                                <ArrowPathIcon className="h-4 w-4 text-slate-400" />
                                                                                Activate Member
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                </div>
                                                            )}

                                                            {currentUser?.role === 'admin' && (
                                                                <div className="px-1.5 pt-1.5 mt-1.5 border-t border-slate-50">
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <button
                                                                                onClick={() => handleDelete(user.id)}
                                                                                className={twMerge(
                                                                                    "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-bold transition-all",
                                                                                    active ? "bg-red-50 text-red-600" : "text-slate-700 hover:text-red-500"
                                                                                )}
                                                                            >
                                                                                <TrashIcon className="h-4 w-4 text-slate-400" />
                                                                                Purge Record
                                                                            </button>
                                                                        )}
                                                                    </Menu.Item>
                                                                </div>
                                                            )}
                                                        </Menu.Items>
                                                    </Transition>
                                                </Menu>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-col md:flex-row items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
                                <div className="text-sm font-semibold text-slate-600">
                                    Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredUsers.length)}</span> of{' '}
                                    <span className="font-black text-slate-900">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'member' : 'members'}
                                </div>

                                {filteredUsers.length > itemsPerPage && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-primary-300 hover:bg-primary-50 active:scale-95'
                                                }`}
                                        >
                                            Previous
                                        </button>

                                        <div className="flex items-center gap-1">
                                            {[...Array(totalPages)].map((_, index) => {
                                                const pageNum = index + 1;
                                                if (
                                                    pageNum === 1 ||
                                                    pageNum === totalPages ||
                                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => setCurrentPage(pageNum)}
                                                            className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                                ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30'
                                                                : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-primary-300 hover:bg-primary-50 active:scale-95'
                                                                }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                    return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                                }
                                                return null;
                                            })}
                                        </div>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-primary-300 hover:bg-primary-50 active:scale-95'
                                                }`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            <MemberDetailsModal
                isOpen={isDetailsModalOpen && modalMode === 'view'}
                onClose={() => setIsDetailsModalOpen(false)}
                member={selectedUser}
                mode="view"
                onUpdate={handleUpdateMember}
            />

            <MemberEditForm
                isOpen={isDetailsModalOpen && modalMode === 'edit'}
                onClose={() => setIsDetailsModalOpen(false)}
                memberId={selectedUser?.id}
                onSave={() => {
                    setIsDetailsModalOpen(false);
                    fetchData();
                }}
            />

            {/* Deactivation Modal */}
            {createPortal(
                <Dialog 
                    open={isDeactivateModalOpen} 
                    onClose={() => setIsDeactivateModalOpen(false)} 
                    className="relative z-[9999]"
                >
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" aria-hidden="true" />
                    
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-md w-full p-6 space-y-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center justify-between">
                                <Dialog.Title className="text-lg font-black text-slate-900 tracking-tight">
                                    {deactivateModalMode === 'approve' ? 'Approve Deactivation' : 'Deactivate Member'}
                                </Dialog.Title>
                                <button
                                    onClick={() => setIsDeactivateModalOpen(false)}
                                    className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-xl hover:bg-slate-50"
                                >
                                    <XCircleIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {deactivateModalMode === 'direct' && (
                                    <div>
                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                            Reason for Deactivation *
                                        </label>
                                        <textarea
                                            value={deactivateReason}
                                            onChange={(e) => setDeactivateReason(e.target.value)}
                                            placeholder="Please enter a reason..."
                                            className="w-full rounded-2xl border-2 border-slate-200 p-3 text-sm focus:border-primary-500 focus:outline-none transition-all resize-none h-24"
                                            required
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Deactivation Schedule
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setDeactivateType('immediate')}
                                            className={twMerge(
                                                "p-3 rounded-2xl border-2 font-bold text-sm transition-all text-center",
                                                deactivateType === 'immediate'
                                                    ? "border-primary-600 bg-primary-50 text-primary-700 shadow-md shadow-primary-600/10"
                                                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                                            )}
                                        >
                                            Immediate
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDeactivateType('scheduled')}
                                            className={twMerge(
                                                "p-3 rounded-2xl border-2 font-bold text-sm transition-all text-center",
                                                deactivateType === 'scheduled'
                                                    ? "border-primary-600 bg-primary-50 text-primary-700 shadow-md shadow-primary-600/10"
                                                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                                            )}
                                        >
                                            Scheduled
                                        </button>
                                    </div>
                                </div>

                                {deactivateType === 'scheduled' && (
                                    <div>
                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                            Deactivation Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={deactivateDate}
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => setDeactivateDate(e.target.value)}
                                            className="w-full rounded-2xl border-2 border-slate-200 p-3 text-sm focus:border-primary-500 focus:outline-none transition-all"
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setIsDeactivateModalOpen(false)}
                                    className="flex-1 px-4 py-3 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all active:scale-98"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitDeactivation}
                                    className="flex-1 px-4 py-3 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-sm shadow-lg shadow-primary-600/20 hover:shadow-primary-700/30 transition-all active:scale-98"
                                >
                                    Confirm
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>,
                document.body
            )}

            {/* Activation Category Conflict Modal */}
            {createPortal(
                <Dialog 
                    open={isActivationCategoryModalOpen} 
                    onClose={() => setIsActivationCategoryModalOpen(false)} 
                    className="relative z-[9999]"
                >
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" aria-hidden="true" />
                    
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-md w-full p-6 space-y-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center justify-between">
                                <Dialog.Title className="text-lg font-black text-slate-900 tracking-tight">
                                    Select New Category
                                </Dialog.Title>
                                <button
                                    onClick={() => setIsActivationCategoryModalOpen(false)}
                                    className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-xl hover:bg-slate-50"
                                >
                                    <XCircleIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <form onSubmit={handleActivateWithCategory} className="space-y-4">
                                <p className="text-sm text-slate-500 font-medium leading-relaxed">
                                    The member's original category is currently selected by another approved member in this chapter. Please choose a different category to activate this user.
                                </p>

                                <div>
                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                        Member Category *
                                    </label>
                                    <select
                                        name="category_id"
                                        value={selectedCategoryId}
                                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-slate-50 border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                                        required
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.displayName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsActivationCategoryModalOpen(false)}
                                        className="flex-1 px-4 py-3 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 transition-all active:scale-98"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-3 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-sm shadow-lg shadow-primary-600/20 hover:shadow-primary-700/30 transition-all active:scale-98"
                                    >
                                        Activate Member
                                    </button>
                                </div>
                            </form>
                        </Dialog.Panel>
                    </div>
                </Dialog>,
                document.body
            )}
        </div>
    );
};

export default ManageUsers;
